import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, Settings, CreditCard, LogOut } from "lucide-react";

/*
 * AccountMenu — account-scoped destinations behind one trigger.
 *
 * Deliberately NOT @radix-ui/react-dropdown-menu: that pulled 35.8 kB
 * gzip into every page's bundle, and its value is roving-tabindex
 * menu semantics this control does not want. Per the ARIA APG this
 * is a *disclosure* containing ordinary links, not a `menu` widget,
 * so links stay links, Tab moves through them normally, and screen
 * readers announce them as links to real pages.
 *
 * Behaviours implemented here: aria-expanded, Escape to close with
 * focus returned to the trigger, click-outside to dismiss, and close
 * on navigation.
 */
export default function AccountMenu({ email, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus(); // focus must not be stranded
      }
    };
    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  const close = () => setOpen(false);
  const itemCls =
    "flex items-center gap-2 w-full px-3 py-2.5 text-sm text-ink hover:bg-surface transition-colors duration-fast";

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className="brutal-btn brutal-btn-ghost max-w-[13rem]"
        aria-expanded={open}
        aria-controls="account-menu"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-account"
      >
        <span className="truncate">{email ?? "Account"}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-fast ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          id="account-menu"
          className="absolute right-0 top-full mt-2 min-w-[15rem] border border-rule bg-canvas shadow-brutal z-overlay"
        >
          <p className="px-3 py-2 border-b border-rule font-mono text-2xs uppercase tracking-widest text-ink-muted truncate">
            {email ?? "Signed in"}
          </p>
          <NavLink to="/settings" className={itemCls} onClick={close} data-testid="nav-settings">
            <Settings size={16} aria-hidden="true" /> Providers
          </NavLink>
          <NavLink to="/pricing" className={itemCls} onClick={close} data-testid="nav-pricing">
            <CreditCard size={16} aria-hidden="true" /> Plan &amp; billing
          </NavLink>
          <button
            type="button"
            onClick={() => { close(); onLogout(); }}
            className={`${itemCls} border-t border-rule text-left`}
            data-testid="nav-logout"
          >
            <LogOut size={16} aria-hidden="true" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
